/**
 * Session Context - MongoDB as SINGLE SOURCE OF TRUTH
 * ====================================================
 * ALL user state comes from MongoDB via backend API.
 * localStorage is NEVER trusted for critical state.
 * 
 * This prevents:
 * - Users manipulating localStorage for free access
 * - State inconsistency across devices
 * - Security vulnerabilities
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import API_URL from '../config/apiConfig';
import { initializePushNotifications, setupForegroundMessageHandler } from '../services/notificationService';
import { syncGateFlagsToServer, restoreGateFlagsFromServer } from '../utils/gateFlags';
import { clearAllTimelineCaches } from '../utils/verifyUserData';
import { rlog } from '../utils/railwayLogger';

const BACKEND_URL = API_URL;

const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [birthDetails, setBirthDetails] = useState(null);
  const [hasBirthDetails, setHasBirthDetails] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [storyData, setStoryData] = useState(null);

  // Check content versions and invalidate stale caches
  const checkContentVersions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content-version`);
      if (!res.ok) {
        rlog('content_version_fetch_failed', { http_status: res.status });
        return;
      }
      const { versions } = await res.json();
      if (!versions) {
        rlog('content_version_empty_response', {});
        return;
      }

      const cachedVersions = JSON.parse(localStorage.getItem('content_versions') || '{}');

      // ── ONE-SHOT MIGRATION: detect legacy integer-based content_versions
      // (pre-2.13.0 clients had {future_reels: 2, ...}). New format is
      // string with hash suffix like "1.f4a37143". Force-wipe ALL section
      // caches once so users immediately get fresh content with the new
      // algorithm — even if individual section comparisons would skip due
      // to missing fields (e.g. past_story not present in cachedVersions).
      const isLegacyFormat = (
        Object.keys(cachedVersions).length > 0 &&
        Object.entries(cachedVersions).some(([k, v]) =>
          k !== 'updated_at' && (typeof v === 'number' || (typeof v === 'string' && !v.includes('.')))
        )
      );
      if (isLegacyFormat) {
        const wipedKeys = [
          'unified_future_story', 'health_future_story_data',
          'ai_content_data', 'domain_insights_cache',
          'detailed_monthly_data', 'real_timeline_data',
          'detailed_yearly_report',
          'ai_story_data', 'unified_story_data', 'audio_cache',
          'audio_generation_complete', 'has_seen_past_story',
          'past_reels_gen_state', 'watching_story',
        ];
        wipedKeys.forEach(k => localStorage.removeItem(k));
        const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
        domains.forEach(d => localStorage.removeItem(`domain_audio_cache_${d}`));
        localStorage.setItem('content_versions', JSON.stringify({ ...versions, updated_at: new Date().toISOString() }));
        rlog('content_version_legacy_format_migrated', {
          old: cachedVersions, new: versions, wiped_count: wipedKeys.length + domains.length,
        });
        // Bail out — caches wiped, fresh content will be fetched on next page mount
        return;
      }

      const changed = [];

      // Only clear caches if we HAD a previous version and it changed
      // Don't clear on first-time (no cached version) — that would wipe freshly loaded data
      if (Object.keys(cachedVersions).length > 0) {
        if (versions.future_reels && cachedVersions.future_reels && versions.future_reels !== cachedVersions.future_reels) {
          console.log('Content update: future_reels version changed, clearing cache');
          changed.push({ kind: 'future_reels', from: cachedVersions.future_reels, to: versions.future_reels });
          localStorage.removeItem('unified_future_story');
          localStorage.removeItem('health_future_story_data');
          const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
          domains.forEach(d => localStorage.removeItem(`domain_audio_cache_${d}`));
        }
        
        if (versions.life_insights && cachedVersions.life_insights && versions.life_insights !== cachedVersions.life_insights) {
          console.log('Content update: life_insights version changed, clearing cache');
          changed.push({ kind: 'life_insights', from: cachedVersions.life_insights, to: versions.life_insights });
          localStorage.removeItem('ai_content_data');
          localStorage.removeItem('domain_insights_cache');
        }
        
        if (versions.timeline_monthly && cachedVersions.timeline_monthly && versions.timeline_monthly !== cachedVersions.timeline_monthly) {
          console.log('Content update: timeline_monthly version changed, clearing cache');
          changed.push({ kind: 'timeline_monthly', from: cachedVersions.timeline_monthly, to: versions.timeline_monthly });
          localStorage.removeItem('detailed_monthly_data');
          localStorage.removeItem('real_timeline_data');
        }
        
        if (versions.timeline_yearly && cachedVersions.timeline_yearly && versions.timeline_yearly !== cachedVersions.timeline_yearly) {
          console.log('Content update: timeline_yearly version changed, clearing cache');
          changed.push({ kind: 'timeline_yearly', from: cachedVersions.timeline_yearly, to: versions.timeline_yearly });
          localStorage.removeItem('detailed_yearly_report');
        }
        
        if (versions.past_story && cachedVersions.past_story && versions.past_story !== cachedVersions.past_story) {
          console.log('Content update: past_story version changed, clearing cache');
          changed.push({ kind: 'past_story', from: cachedVersions.past_story, to: versions.past_story });
          localStorage.removeItem('ai_story_data');
          localStorage.removeItem('unified_story_data');
          localStorage.removeItem('audio_cache');
          localStorage.removeItem('audio_generation_complete');
          localStorage.removeItem('has_seen_past_story');
          localStorage.removeItem('past_reels_gen_state');
          localStorage.removeItem('watching_story');
        }
      }
      
      if (changed.length) {
        rlog('content_version_changed', {
          changed_count: changed.length,
          changes: changed,
          new_versions: versions,
          algo_story_to: versions.algo_story || null,
          algo_insights_to: versions.algo_insights || null,
          wiped_keys: changed.flatMap(c => ({
            future_reels: ['unified_future_story','health_future_story_data','domain_audio_cache_*'],
            life_insights: ['ai_content_data','domain_insights_cache'],
            timeline_monthly: ['detailed_monthly_data','real_timeline_data'],
            timeline_yearly: ['detailed_yearly_report'],
            past_story: ['ai_story_data','unified_story_data','audio_cache','past_reels_gen_state'],
          }[c.kind] || [])),
        });
      } else if (Object.keys(cachedVersions).length > 0) {
        // Already up-to-date — log a heartbeat so Railway can confirm the device polled
        rlog('content_version_uptodate', {
          algo_story: versions.algo_story || null,
          algo_insights: versions.algo_insights || null,
        });
      }
      
      // Save current versions
      localStorage.setItem('content_versions', JSON.stringify(versions));
    } catch (e) {
      // Log error so we can debug silent failures in production
      try { rlog('content_version_check_error', { error: String(e?.message || e).slice(0, 200) }); } catch {}
    }
  };

  // Sync story data from MongoDB cloud after app reinstall/update
  // Non-blocking: runs in background, doesn't block session load
  const syncStoryFromCloud = async (userId) => {
    try {
      // Skip if localStorage already has story data (no need to sync)
      const existingStory = localStorage.getItem('unified_story_data');
      if (existingStory) {
        try {
          const parsed = JSON.parse(existingStory);
          if (parsed && parsed.past_story) {
            return; // Already have valid story data locally
          }
        } catch (e) { /* corrupted, proceed to sync */ }
      }

      // Skip if no user_id
      if (!userId) return;

      console.log('☁️ localStorage empty — syncing story from MongoDB...');
      const deviceId = localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id');
      const url = `${BACKEND_URL}/api/unified-story/load/${userId}${deviceId ? `?device_id=${deviceId}` : ''}`;
      
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) return;
      
      const result = await res.json();
      if (!result.success || !result.data) {
        console.log('☁️ No cloud story found for user — fresh start');
        return;
      }

      const storyData = result.data;
      console.log('☁️ Cloud story found! Restoring to localStorage...');

      // Restore unified_story_data
      try {
        localStorage.setItem('unified_story_data', JSON.stringify(storyData));
      } catch (e) { console.warn('Sync: unified_story_data quota exceeded'); }

      // Rebuild ai_story_data from unified story (same logic as LoadingStoryGenerator)
      if (storyData.past_story) {
        const userName = storyData.user_info?.name || storyData.past_story?.mahadashas?.[0]?.domain_predictions?.career?.summary?.split(' ')?.[0] || 'User';
        const aiContentData = {
          ai_generated: true,
          generated_at: new Date().toISOString(),
          user_info: { name: userName },
          past_story: storyData.past_story,
          all_mahadashas: storyData.all_mahadashas || [],
          past_mahadashas: storyData.past_mahadashas || storyData.past_story?.mahadashas || [],
          current_mahadasha: storyData.current_mahadasha || {},
          future_mahadashas: storyData.future_mahadashas || [],
          sections: storyData.sections || [],
          kernel: storyData.kernel || {},
          meta: { generated_at: new Date().toISOString(), synced_from_cloud: true }
        };
        try {
          localStorage.setItem('ai_story_data', JSON.stringify(aiContentData));
        } catch (e) { /* quota */ }
      }

      // Restore domain insights if available
      if (storyData.domain_insights) {
        try {
          localStorage.setItem('domain_insights_cache', JSON.stringify(storyData.domain_insights));
        } catch (e) { /* quota */ }
      }

      // Restore future story if available
      if (storyData.future_story) {
        try {
          localStorage.setItem('unified_future_story', JSON.stringify({
            mahadashas: storyData.future_story.mahadashas || [],
            intro: storyData.future_story.intro || {},
            timeline_narrations: storyData.future_story.timeline_narrations || {},
            domains: storyData.domains
          }));
        } catch (e) { /* quota */ }
      }

      // Restore cache_key if available
      if (storyData.cache_key) {
        localStorage.setItem('story_cache_key', storyData.cache_key);
      }

      // Mark story as viewed/generated so dashboard shows correctly
      localStorage.setItem('past_story_viewed', 'true');
      localStorage.setItem('onboarding_completed', 'true');
      localStorage.setItem('story_generated', 'true');

      console.log('☁️ Story data synced from cloud successfully!');
    } catch (e) {
      console.log('☁️ Cloud sync skipped:', e.message);
    }
  };

  // Initialize push notifications (non-blocking)
  const initializeNotifications = async (userId) => {
    try {
      // Only init if not already done
      if (localStorage.getItem('notifications_initialized') === userId) {
        return;
      }
      
      const success = await initializePushNotifications(userId);
      if (success) {
        localStorage.setItem('notifications_initialized', userId);
        console.log('🔔 Push notifications enabled');
        
        // Setup foreground message handler
        setupForegroundMessageHandler((payload) => {
          console.log('📩 Foreground notification:', payload);
        });
      }
    } catch (error) {
      console.log('Notification init skipped:', error.message);
    }
  };

  // Fetch session from MongoDB (SINGLE SOURCE OF TRUTH)
  const fetchSession = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Check server reset version - force fresh start if version bumped
      try {
        const healthRes = await fetch(`${BACKEND_URL}/api/health`, { method: 'GET' });
        const healthData = await healthRes.json();
        const serverResetVersion = healthData.reset_version || 0;
        const localResetVersion = parseInt(localStorage.getItem('app_reset_version') || '0');
        if (serverResetVersion > localResetVersion) {
          console.log('🔄 Server reset detected - clearing all local data');
          // Preserve critical user session and payment data during reset
          const keysToKeep = [
            'device_id', 'taraanaya_device_id',
            'birthDetails', 'birth_details', 'completeBirthData', 'birthData',
            'is_premium', 'isPremium', 'sage_credits', 'plan_unlocks', 'plan_id',
            'subscriptionData', 'premium_expires',
            'user_id', 'session_id', 'user_name', 'audio_user_id',
            'future_reels_generating', 'future_reels_ready',
            'past_story_viewed', 'free_story_used',
            'onboarding_form_draft', 'onboarding_in_progress', 'story_generation_in_progress',
            'onboarding_completed', 'watching_story', 'unified_story_data', 'ai_story_data',
            'ps_selected_language', 'ps_selected_domain', 'ps_selected_mahadasha',
            'fresh_start_version', 'app_reset_version',
            // TTS generation state — critical for sequential audio generation
            'audio_ready_domains', 'domain_progress', 'domain_tts_status',
            'tts_generation_triggered', 'tts_currently_generating', 'tts_user_id',
            'tts_generation_started', 'tts_ultra_fast_complete',
            'future_reels_gen_start',
          ];
          const savedValues = {};
          keysToKeep.forEach(k => { savedValues[k] = localStorage.getItem(k); });
          localStorage.clear();
          keysToKeep.forEach(k => { if (savedValues[k]) localStorage.setItem(k, savedValues[k]); });
          localStorage.setItem('app_reset_version', String(serverResetVersion));
          // Reset React state directly — do NOT call clearLocalState (it would wipe app_reset_version)
          setIsAuthenticated(false);
          setUser(null);
          setBirthDetails(null);
          setHasBirthDetails(false);
          setIsPremium(false);
          setSubscription(null);
          setStoryData(null);
          // Restore premium/birth from preserved keys
          const restoredPremium = savedValues['is_premium'] === 'true' || savedValues['isPremium'] === 'true';
          if (restoredPremium) setIsPremium(true);
          if (savedValues['birthDetails']) {
            try {
              const parsed = JSON.parse(savedValues['birthDetails']);
              setBirthDetails(parsed);
              setHasBirthDetails(true);
            } catch(e) {}
          }
          setIsLoading(false);
          return false;
        }
        
        // Check algorithm version - force story regeneration if algorithm changed
        const serverAlgoVersion = healthData.algorithm_version || 0;
        const localAlgoVersion = parseInt(localStorage.getItem('algorithm_version') || '0');
        if (serverAlgoVersion > localAlgoVersion) {
          console.log('Algorithm update detected - clearing ALL story caches for fresh predictions');
          // Clear ALL story-related caches including past story and audio
          localStorage.removeItem('unified_story_data');
          localStorage.removeItem('ai_story_data');
          localStorage.removeItem('ai_content_data');
          localStorage.removeItem('ai_content_english');
          localStorage.removeItem('ai_content_hindi');
          localStorage.removeItem('ai_content_hinglish');
          localStorage.removeItem('story_cache_key');
          localStorage.removeItem('content_versions');
          localStorage.removeItem('audio_cache');
          localStorage.removeItem('audio_generation_complete');
          localStorage.removeItem('past_reels_gen_state');
          localStorage.removeItem('watching_story');
          localStorage.removeItem('tts_currently_generating');
          localStorage.removeItem('audio_ready_domains');
          localStorage.removeItem('domain_progress');
          localStorage.removeItem('unified_future_story');
          localStorage.removeItem('domain_insights_cache');
          localStorage.removeItem('detailed_yearly_report');
          localStorage.removeItem('detailed_monthly_data');
          localStorage.removeItem('real_timeline_data');
          // Clear per-domain audio caches
          const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
          domains.forEach(d => localStorage.removeItem(`domain_audio_cache_${d}`));
          // Clear any story-generation flags so fresh generation triggers
          localStorage.removeItem('has_seen_past_story');
          localStorage.removeItem('future_reels_ready');
          localStorage.removeItem('future_reels_generating');
          localStorage.setItem('algorithm_version', String(serverAlgoVersion));
          // NOTE: We do NOT clear premium/payment keys - user keeps their purchased packs
        }
      } catch(e) { /* health check failed, continue normally */ }
      
      // Get device_id for anonymous users
      const deviceId = localStorage.getItem('device_id') || `device_${Date.now()}`;
      localStorage.setItem('device_id', deviceId);
      
      // Add timeout to prevent infinite loading (5 seconds max)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-ID': deviceId
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      if (data.success && data.authenticated) {
        // ALL state comes from MongoDB
        setIsAuthenticated(true);
        setUser(data.user);
        setBirthDetails(data.birth_details);
        setHasBirthDetails(data.has_birth_details);
        
        // Premium: backend is source of truth, but also check localStorage (for mock payment)
        const backendPremium = data.is_premium || false;
        const localPremium = localStorage.getItem('is_premium') === 'true' || localStorage.getItem('isPremium') === 'true';
        setIsPremium(backendPremium || localPremium);
        
        // If backend confirms premium, restore all premium localStorage keys
        // so isFeatureUnlocked() and other localStorage-based checks work after relogin
        if (backendPremium && data.subscription) {
          const sub = data.subscription;
          localStorage.setItem('is_premium', 'true');
          localStorage.setItem('isPremium', 'true');
          
          // Restore unlocks - derive from plan_id if not stored in subscription
          let unlocks = sub.unlocks;
          if (!unlocks || !Array.isArray(unlocks) || unlocks.length === 0) {
            const planUnlocksMap = {
              'starter': ['life_insights', 'sage_questions'],
              'basic': ['life_insights', 'sage_questions'],
              'com.taraanaya.starter': ['life_insights', 'sage_questions'],
              'com.taraanaya.starter_199': ['life_insights', 'sage_questions'],
              'growth': ['life_insights', 'sage_questions', 'future_reels'],
              'pro': ['life_insights', 'sage_questions', 'future_reels'],
              'premium': ['life_insights', 'sage_questions', 'future_reels'],
              'com.taraanaya.growth': ['life_insights', 'sage_questions', 'future_reels'],
              'com.taraanaya.premium_599': ['life_insights', 'sage_questions', 'future_reels'],
              'ultimate': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
              'monthly': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
              'com.taraanaya.monthly': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
              'com.taraanaya.ultimate_999': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
            };
            unlocks = planUnlocksMap[sub.plan_id] || ['life_insights', 'sage_questions'];
          }
          localStorage.setItem('plan_unlocks', JSON.stringify(unlocks));
          
          if (sub.credits) localStorage.setItem('sage_credits', String(sub.credits));
          if (sub.expires_at) localStorage.setItem('premium_expires', sub.expires_at);
          if (sub.plan_id) localStorage.setItem('plan_id', sub.plan_id);
        }
        
        setSubscription(data.subscription);
        
        // Store user_id for API calls (not for state!)
        localStorage.setItem('user_id', data.user.user_id);
        
        // Also cache birth details in localStorage for components that need it
        // (Timeline, Insights, etc. still read from localStorage)
        if (data.birth_details) {
          // Detect if birth details changed (different person or re-onboarding)
          const oldBirthDetails = localStorage.getItem('birthDetails');
          let nameChanged = false;
          if (oldBirthDetails) {
            try {
              const old = JSON.parse(oldBirthDetails);
              if (old.name !== data.birth_details.name || old.dateOfBirth !== data.birth_details.dateOfBirth) {
                nameChanged = true;
                console.log('Birth details changed! Clearing ALL stale content caches...');
              }
            } catch(e) { nameChanged = true; }
          } else {
            // No old birth details — this is first load, check if cached content matches
            nameChanged = true; // Clear to be safe on first session
          }
          
          if (nameChanged) {
            // Clear ALL content caches to force fresh generation with correct name
            ['unified_story_data', 'ai_content_data', 'ai_content_english', 
             'ai_content_hindi', 'ai_content_hinglish', 'story_cache_key',
             'domain_insights_cache', 'timeline_data_cache', 'past_story_cache',
             'future_story_cache', 'hex_story_cache', 'dashboard_cache',
             'daily_prediction_cache', 'detailed_yearly_report', 'detailed_monthly_data',
             'real_timeline_data', 'content_versions', 'unified_future_story'
            ].forEach(k => localStorage.removeItem(k));
            // Clear individual domain timeline caches (health_timeline_data, career_timeline_data, etc.)
            clearAllTimelineCaches();
            // Also clear per-user report caches (detailed_report_{userId})
            const keys = Object.keys(localStorage);
            keys.forEach(k => {
              if (k.startsWith('detailed_report_') || k.startsWith('timeline_') || 
                  k.startsWith('insights_') || k.startsWith('ai_content_')) {
                localStorage.removeItem(k);
              }
            });
          }
          
          localStorage.setItem('birthDetails', JSON.stringify(data.birth_details));
          localStorage.setItem('birth_details', JSON.stringify(data.birth_details));
        }
        
        console.log('✅ Session loaded from MongoDB:', {
          user: data.user?.user_id,
          isPremium: data.is_premium,
          hasBirthDetails: data.has_birth_details
        });
        
        // Initialize push notifications for the user (non-blocking)
        initializeNotifications(data.user.user_id);
        
        // Check content versions (invalidate stale cache)
        checkContentVersions();
        
        // ═══════════════════════════════════════════════════════════════
        // SYNC FROM CLOUD: Restore story data from MongoDB after reinstall
        // If localStorage is empty (app uninstall/reinstall/update), but
        // user has stories in MongoDB, fetch and repopulate localStorage.
        // This ensures paid users don't lose access to generated stories.
        // ═══════════════════════════════════════════════════════════════
        syncStoryFromCloud(data.user.user_id);
        
        // Restore gate flags from server (past_story_viewed, free_story_used)
        if (data.user?.gate_flags) {
          restoreGateFlagsFromServer(data.user.gate_flags);
        }
        // Sync gate flags bidirectionally
        syncGateFlagsToServer(data.user.user_id);
        
        // Periodic premium verification — re-check from server every 5 min
        // This catches: expired subscriptions, localStorage manipulation, sync issues
        const premiumCheckInterval = setInterval(async () => {
          try {
            const resp = await fetch(`${API_URL}/api/auth/verify-premium/${data.user.user_id}`);
            if (resp.ok) {
              const pData = await resp.json();
              setIsPremium(pData.is_premium || false);
              if (pData.is_premium) {
                localStorage.setItem('is_premium', 'true');
                if (pData.plan_unlocks) localStorage.setItem('plan_unlocks', JSON.stringify(pData.plan_unlocks));
                if (pData.sage_credits !== undefined) localStorage.setItem('sage_credits', String(pData.sage_credits));
              } else {
                localStorage.removeItem('is_premium');
                localStorage.removeItem('isPremium');
                localStorage.removeItem('plan_unlocks');
              }
            }
          } catch (e) { /* network error — keep cached state */ }
        }, 5 * 60 * 1000); // Every 5 minutes
        
        // Cleanup interval on unmount
        if (window._premiumCheckInterval) clearInterval(window._premiumCheckInterval);
        window._premiumCheckInterval = premiumCheckInterval;
        
        return true;
      } else {
        // Not authenticated via backend session - check localStorage fallback
        // This handles device-based users who paid without Firebase login
        const localPremium = localStorage.getItem('isPremium') === 'true' || localStorage.getItem('is_premium') === 'true';
        const localBirthDetails = localStorage.getItem('birthDetails');
        const deviceId = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
        
        if (localPremium || localBirthDetails) {
          // Device-based user with local data - don't clear everything
          if (localPremium) {
            setIsPremium(true);
            try {
              const subData = localStorage.getItem('subscriptionData');
              if (subData) setSubscription(JSON.parse(subData));
            } catch(e) {}
          }
          if (localBirthDetails) {
            try {
              setBirthDetails(JSON.parse(localBirthDetails));
              setHasBirthDetails(true);
            } catch(e) {}
          }
          if (deviceId) {
            setUser({ user_id: deviceId, name: '' });
          }
          console.log('📱 Device-based session restored from localStorage');
        } else {
          clearLocalState();
        }
        return false;
      }
    } catch (error) {
      console.error('Session fetch error:', error);
      // Don't clear local state on network error - preserve device-based session
      const localPremium = localStorage.getItem('isPremium') === 'true' || localStorage.getItem('is_premium') === 'true';
      if (localPremium) {
        setIsPremium(true);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Clear ALL state (React + localStorage)
  // NOTE: past_story_viewed is NEVER cleared - once viewed, always viewed on this device
  // isFullLogout: true = clear auth keys too (logout), false = preserve auth keys (session refresh)
  const clearLocalState = (isFullLogout = false) => {
    // Clear React state
    setIsAuthenticated(false);
    setUser(null);
    setBirthDetails(null);
    setHasBirthDetails(false);
    setIsPremium(false);
    setSubscription(null);
    setStoryData(null);
    
    // Preserve critical device-level flags
    const deviceId = localStorage.getItem('device_id');
    const taraanayaDeviceId = localStorage.getItem('taraanaya_device_id');
    const pastStoryViewed = localStorage.getItem('past_story_viewed');
    const freeStoryUsed = localStorage.getItem('free_story_used');
    
    // Preserve auth/session data (CRITICAL for app resume - skip during logout)
    const isAuthVal = isFullLogout ? null : localStorage.getItem('isAuthenticated');
    const userIdVal = isFullLogout ? null : localStorage.getItem('user_id');
    const sessionIdVal = isFullLogout ? null : localStorage.getItem('session_id');
    const audioUserIdVal = isFullLogout ? null : localStorage.getItem('audio_user_id');
    
    // Preserve story session flags (for resume functionality — NOT during logout)
    const watchingStory = localStorage.getItem('watching_story');
    const unifiedStoryData = isFullLogout ? null : localStorage.getItem('unified_story_data');
    const birthDetailsData = isFullLogout ? null : localStorage.getItem('birthDetails');
    const birthDetailsAlt = isFullLogout ? null : localStorage.getItem('birth_details');
    const freshStartVersion = localStorage.getItem('fresh_start_version');
    
    // Preserve version tracking keys (prevents repeated cache wipes on every reopen)
    const appResetVersion = localStorage.getItem('app_reset_version');
    const algorithmVersion = localStorage.getItem('algorithm_version');
    const contentVersions = localStorage.getItem('content_versions');
    
    // Preserve cached content data ALWAYS (per-user content, not per-session).
    // Updates are handled by checkContentVersions() — invalidating only what
    // actually changed. Logging out + back in for the SAME user should NOT
    // wipe stories/insights/timelines (otherwise everything regenerates).
    // If a DIFFERENT user logs in, the birth-details mismatch logic at line
    // ~434 wipes everything anyway.
    const aiStoryData = localStorage.getItem('ai_story_data');
    const aiContentData = localStorage.getItem('ai_content_data');
    const aiContentEnglish = localStorage.getItem('ai_content_english');
    const aiContentHindi = localStorage.getItem('ai_content_hindi');
    const aiContentHinglish = localStorage.getItem('ai_content_hinglish');
    const unifiedStoryDataPreserved = localStorage.getItem('unified_story_data');
    const unifiedFutureStoryPreserved = localStorage.getItem('unified_future_story');
    const detailedYearlyReport = localStorage.getItem('detailed_yearly_report');
    const detailedMonthlyData = localStorage.getItem('detailed_monthly_data');
    const realTimelineData = localStorage.getItem('real_timeline_data');
    const domainReportCache = localStorage.getItem('domain_report_cache');
    const domainInsightsCache = localStorage.getItem('domain_insights_cache');
    const audioCache = localStorage.getItem('audio_cache');

    // Premium/payment + TTS state should survive session clear but be cleared
    // on full logout (so server is single source of truth on next login).
    const isPremiumVal = isFullLogout ? null : localStorage.getItem('is_premium');
    const isPremiumAlt = isFullLogout ? null : localStorage.getItem('isPremium');
    const planUnlocks = isFullLogout ? null : localStorage.getItem('plan_unlocks');
    const premiumExpires = isFullLogout ? null : localStorage.getItem('premium_expires');
    const sageCredits = isFullLogout ? null : localStorage.getItem('sage_credits');
    const storyCacheKey = isFullLogout ? null : localStorage.getItem('story_cache_key');
    const planId = isFullLogout ? null : localStorage.getItem('plan_id');

    // Preserve TTS generation state (CRITICAL - audio generation progress must survive session clear)
    const audioReadyDomains = isFullLogout ? null : localStorage.getItem('audio_ready_domains');
    const domainProgress = isFullLogout ? null : localStorage.getItem('domain_progress');
    const domainTtsStatus = isFullLogout ? null : localStorage.getItem('domain_tts_status');
    const ttsGenerationTriggered = isFullLogout ? null : localStorage.getItem('tts_generation_triggered');
    const ttsCurrentlyGenerating = isFullLogout ? null : localStorage.getItem('tts_currently_generating');
    const ttsUserId = isFullLogout ? null : localStorage.getItem('tts_user_id');
    const ttsGenerationStarted = isFullLogout ? null : localStorage.getItem('tts_generation_started');
    const ttsUltraFastComplete = isFullLogout ? null : localStorage.getItem('tts_ultra_fast_complete');
    const futureReelsGenStart = isFullLogout ? null : localStorage.getItem('future_reels_gen_start');
    
    // Clear all localStorage
    localStorage.clear();
    
    // Restore device-level flags (NEVER delete these)
    if (deviceId) localStorage.setItem('device_id', deviceId);
    if (taraanayaDeviceId) localStorage.setItem('taraanaya_device_id', taraanayaDeviceId);
    if (pastStoryViewed) localStorage.setItem('past_story_viewed', pastStoryViewed);
    if (freeStoryUsed) localStorage.setItem('free_story_used', freeStoryUsed);
    
    // Restore auth/session data
    if (isAuthVal) localStorage.setItem('isAuthenticated', isAuthVal);
    if (userIdVal) localStorage.setItem('user_id', userIdVal);
    if (sessionIdVal) localStorage.setItem('session_id', sessionIdVal);
    if (audioUserIdVal) localStorage.setItem('audio_user_id', audioUserIdVal);
    
    // Restore story session flags (for resume on reopen)
    if (watchingStory) localStorage.setItem('watching_story', watchingStory);
    if (unifiedStoryData) localStorage.setItem('unified_story_data', unifiedStoryData);
    if (birthDetailsData) localStorage.setItem('birthDetails', birthDetailsData);
    if (birthDetailsAlt) localStorage.setItem('birth_details', birthDetailsAlt);
    if (freshStartVersion) localStorage.setItem('fresh_start_version', freshStartVersion);
    
    // Restore version tracking keys
    if (appResetVersion) localStorage.setItem('app_reset_version', appResetVersion);
    if (algorithmVersion) localStorage.setItem('algorithm_version', algorithmVersion);
    if (contentVersions) localStorage.setItem('content_versions', contentVersions);
    
    // Restore cached content data (preserved for both session-clear AND logout)
    if (aiStoryData) localStorage.setItem('ai_story_data', aiStoryData);
    if (aiContentData) localStorage.setItem('ai_content_data', aiContentData);
    if (aiContentEnglish) localStorage.setItem('ai_content_english', aiContentEnglish);
    if (aiContentHindi) localStorage.setItem('ai_content_hindi', aiContentHindi);
    if (aiContentHinglish) localStorage.setItem('ai_content_hinglish', aiContentHinglish);
    if (unifiedStoryDataPreserved) localStorage.setItem('unified_story_data', unifiedStoryDataPreserved);
    if (unifiedFutureStoryPreserved) localStorage.setItem('unified_future_story', unifiedFutureStoryPreserved);
    if (detailedYearlyReport) localStorage.setItem('detailed_yearly_report', detailedYearlyReport);
    if (detailedMonthlyData) localStorage.setItem('detailed_monthly_data', detailedMonthlyData);
    if (realTimelineData) localStorage.setItem('real_timeline_data', realTimelineData);
    if (domainReportCache) localStorage.setItem('domain_report_cache', domainReportCache);
    if (domainInsightsCache) localStorage.setItem('domain_insights_cache', domainInsightsCache);
    if (audioCache) localStorage.setItem('audio_cache', audioCache);
    
    // Restore premium/payment data
    if (isPremiumVal) localStorage.setItem('is_premium', isPremiumVal);
    if (isPremiumAlt) localStorage.setItem('isPremium', isPremiumAlt);
    if (planUnlocks) localStorage.setItem('plan_unlocks', planUnlocks);
    if (premiumExpires) localStorage.setItem('premium_expires', premiumExpires);
    if (sageCredits) localStorage.setItem('sage_credits', sageCredits);
    if (storyCacheKey) localStorage.setItem('story_cache_key', storyCacheKey);
    if (planId) localStorage.setItem('plan_id', planId);
    
    // Restore TTS generation state
    if (audioReadyDomains) localStorage.setItem('audio_ready_domains', audioReadyDomains);
    if (domainProgress) localStorage.setItem('domain_progress', domainProgress);
    if (domainTtsStatus) localStorage.setItem('domain_tts_status', domainTtsStatus);
    if (ttsGenerationTriggered) localStorage.setItem('tts_generation_triggered', ttsGenerationTriggered);
    if (ttsCurrentlyGenerating) localStorage.setItem('tts_currently_generating', ttsCurrentlyGenerating);
    if (ttsUserId) localStorage.setItem('tts_user_id', ttsUserId);
    if (ttsGenerationStarted) localStorage.setItem('tts_generation_started', ttsGenerationStarted);
    if (ttsUltraFastComplete) localStorage.setItem('tts_ultra_fast_complete', ttsUltraFastComplete);
    if (futureReelsGenStart) localStorage.setItem('future_reels_gen_start', futureReelsGenStart);
    
    // Restore React state from preserved premium data
    const restoredPremium = isPremiumVal === 'true' || isPremiumAlt === 'true';
    if (restoredPremium) {
      setIsPremium(true);
    }
    const restoredBirth = birthDetailsData;
    if (restoredBirth) {
      try {
        const parsed = JSON.parse(restoredBirth);
        setBirthDetails(parsed);
        setHasBirthDetails(true);
      } catch(e) { /* ignore */ }
    }
    // Restore authentication if user has premium or birth details
    if (restoredPremium || restoredBirth) {
      setIsAuthenticated(true);
      if (deviceId) setUser({ user_id: deviceId, name: '' });
    }
    
    console.log('🗑️ Session cleared - device IDs, story flags, premium data preserved' + (restoredPremium ? ' (premium restored)' : ''));
  };

  // Logout - clear backend session + local state
  const logout = async () => {
    rlog('logout_start', {
      user_id: user?.user_id,
      was_premium: isPremium,
      plan_id: subscription?.plan_id,
      credits_before: localStorage.getItem('sage_credits'),
    });
    // Fire-and-forget — don't block UI waiting for backend
    fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST' }).catch(() => {});
    clearLocalState(true); // Full logout - clear auth + premium keys immediately
    rlog('logout_done', {});
  };

  // Update session after successful login (data comes from backend)
  const updateSessionFromLogin = (loginData) => {
    if (loginData.success) {
      setIsAuthenticated(true);
      setUser({
        user_id: loginData.user_id,
        name: loginData.name,
        email: loginData.email,
        phone: loginData.phone,
        picture: loginData.picture
      });
      setBirthDetails(loginData.birth_details);
      setHasBirthDetails(loginData.has_birth_details);
      setIsPremium(loginData.is_premium || false);
      setSubscription(loginData.subscription);
      
      // Store user_id for API calls
      localStorage.setItem('user_id', loginData.user_id);
      
      // Cache birth details in localStorage for components that need it
      if (loginData.birth_details) {
        localStorage.setItem('birthDetails', JSON.stringify(loginData.birth_details));
        localStorage.setItem('birth_details', JSON.stringify(loginData.birth_details));
      }
      
      // CRITICAL: Restore premium/payment localStorage keys from backend subscription data
      // This ensures dashboard shows correct lock/unlock states after logout + re-login
      if (loginData.is_premium && loginData.subscription) {
        const sub = loginData.subscription;
        localStorage.setItem('is_premium', 'true');
        localStorage.setItem('isPremium', 'true');
        
        if (sub.plan_id) localStorage.setItem('plan_id', sub.plan_id);
        if (sub.expires_at) localStorage.setItem('premium_expires', sub.expires_at);
        
        // Restore unlocks - derive from plan_id if not stored
        let unlocks = sub.unlocks;
        if (!unlocks || !Array.isArray(unlocks) || unlocks.length === 0) {
          const planUnlocksMap = {
            'starter': ['life_insights', 'sage_questions'],
            'basic': ['life_insights', 'sage_questions'],
            'com.taraanaya.starter': ['life_insights', 'sage_questions'],
            'growth': ['life_insights', 'sage_questions', 'future_reels'],
            'pro': ['life_insights', 'sage_questions', 'future_reels'],
            'com.taraanaya.growth': ['life_insights', 'sage_questions', 'future_reels'],
            'monthly': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
            'premium': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
            'com.taraanaya.monthly': ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
          };
          unlocks = planUnlocksMap[sub.plan_id] || ['life_insights', 'sage_questions'];
        }
        localStorage.setItem('plan_unlocks', JSON.stringify(unlocks));
        
        // Restore sage credits
        if (sub.credits) localStorage.setItem('sage_credits', String(sub.credits));
      }
      
      console.log('✅ Session updated from login:', {
        user: loginData.user_id,
        isPremium: loginData.is_premium,
        hasBirthDetails: loginData.has_birth_details
      });
      rlog('login_hydrated', {
        user_id: loginData.user_id,
        is_premium: !!loginData.is_premium,
        plan_id: loginData.subscription?.plan_id,
        has_birth_details: !!loginData.has_birth_details,
        credits: loginData.subscription?.credits,
        unlocks_count: (loginData.subscription?.unlocks || []).length,
      });
      
      // Sync story data from cloud (non-blocking) — handles reinstall scenario
      syncStoryFromCloud(loginData.user_id);
      
      // 📡 Hydrate UI state (dashboard tab, future-reels progress, timeline cursor, prefs)
      // from MongoDB → localStorage. Critical for reinstall/update continuity.
      (async () => {
        try {
          const { hydrateUserState } = await import('../utils/userStateSync');
          await hydrateUserState();
        } catch (e) {
          console.log('[SessionContext] userStateSync hydrate skipped:', e?.message);
        }
      })();
      
      // Restore gate flags from server (past_story_viewed, free_story_used)
      // Survives uninstall/reinstall since stored in MongoDB
      if (loginData.gate_flags) {
        restoreGateFlagsFromServer(loginData.gate_flags);
      }
      
      // 💰 CRITICAL: If backend says no premium BUT we're on native platform
      // → trigger IAP restore + reconcile. This catches:
      //   (a) user paid before login completed → orphan user_id="unknown" subscription
      //   (b) user deleted account and re-logged in with same Google/email
      // The reconcile endpoint claims orphan transactions to the current user.
      if (!loginData.is_premium && typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
        (async () => {
          try {
            console.log('💎 No premium on fresh login — attempting IAP reconcile + restore...');
            const { restorePurchases } = await import('../services/iapService');
            // restorePurchases now also calls /api/iap/reconcile internally
            await restorePurchases(loginData.user_id).catch((e) => {
              console.log('IAP restore failed (not critical):', e?.message || e);
            });
            // Poll for subscription to propagate after restore triggers backend verification
            await pollSubscriptionUntilPremium(30, 3);
          } catch (e) {
            console.log('IAP restore error:', e);
          }
        })();
      } else if (loginData.user_id) {
        // Even if premium, run reconcile once to backfill email/phone on sub records
        (async () => {
          try {
            await fetch(`${BACKEND_URL}/api/iap/reconcile`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: loginData.user_id,
                email: loginData.email || '',
                phone: loginData.phone || '',
                device_id: localStorage.getItem('taraanaya_device_id') || '',
                session_id: localStorage.getItem('session_id') || '',
              }),
            }).catch(() => {});
          } catch {}
        })();
      }
      
      // 💳 Razorpay payment recovery: if user has pending razorpay_transactions
      // (status=created) but no subscription, try to recover them by querying Razorpay live.
      // Handles the "payment succeeded but verify-payment call failed" edge case.
      if (!loginData.is_premium && loginData.user_id) {
        (async () => {
          try {
            const res = await fetch(`${BACKEND_URL}/api/razorpay/recover-payment/${loginData.user_id}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            if (data.success && data.recovered > 0) {
              console.log(`💳 Recovered ${data.recovered} pending payment(s):`, data.details);
              // Refresh subscription state
              await refreshSubscription();
            }
          } catch (e) {
            console.log('Payment recovery skipped:', e?.message);
          }
        })();
      }
    }
  };

  // Update premium status after payment (called from payment page)
  const updatePremiumStatus = async (isPremiumNow, subscriptionData = null) => {
    rlog('update_premium_status', {
      new_premium: isPremiumNow,
      prev_premium: isPremium,
      new_plan: subscriptionData?.plan_id,
      prev_plan: subscription?.plan_id,
      new_credits: subscriptionData?.credits,
      has_sub_data: !!subscriptionData,
    });
    setIsPremium(isPremiumNow);
    if (subscriptionData) {
      setSubscription(subscriptionData);
    }
    
    // Store in localStorage as fallback for device-based (no-login) users
    if (isPremiumNow) {
      localStorage.setItem('isPremium', 'true');
      localStorage.setItem('is_premium', 'true');
      if (subscriptionData) {
        localStorage.setItem('subscriptionData', JSON.stringify(subscriptionData));
        // Immediately reflect credits across all sections
        if (subscriptionData.credits) {
          localStorage.setItem('sage_credits', String(subscriptionData.credits));
          try { window.dispatchEvent(new Event('credits-updated')); } catch {}
        }
        if (subscriptionData.plan_id) {
          localStorage.setItem('plan_id', subscriptionData.plan_id);
        }
        if (subscriptionData.unlocks) {
          localStorage.setItem('plan_unlocks', JSON.stringify(subscriptionData.unlocks));
        }
      }
    }
    
    // Also update in MongoDB
    if (user?.user_id && isPremiumNow) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/users/${user.user_id}/premium`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_premium: true, subscription: subscriptionData })
        });
      } catch (e) {
        console.log('Update premium error:', e);
      }
    }
    
    console.log('💎 Premium status updated:', isPremiumNow);
  };

  // Refresh subscription status from MongoDB
  const refreshSubscription = async () => {
    if (!user?.user_id) return false;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/razorpay/subscription/${user.user_id}`);
      const data = await response.json();
      
      if (data.success && data.subscription) {
        const status = data.subscription.status;
        const premium = status === 'active' || status === 'paid' || status === 'completed';
        const prevPremium = isPremium;
        const prevPlan = subscription?.plan_id;
        rlog('refresh_subscription', {
          user_id: user.user_id,
          status,
          new_premium: premium,
          prev_premium: prevPremium,
          new_plan: data.subscription.plan_id,
          prev_plan: prevPlan,
          new_credits: data.subscription.credits,
          unlocks_count: (data.subscription.unlocks || []).length,
          expires_at: data.subscription.expires_at,
          state_changed: prevPremium !== premium || prevPlan !== data.subscription.plan_id,
        });
        setIsPremium(premium);
        setSubscription(data.subscription);
        
        // Also sync to localStorage so Dashboard/feature checks work
        if (premium) {
          localStorage.setItem('is_premium', 'true');
          localStorage.setItem('isPremium', 'true');
          if (data.subscription.unlocks) {
            localStorage.setItem('plan_unlocks', JSON.stringify(data.subscription.unlocks));
          }
          if (data.subscription.plan_id) {
            localStorage.setItem('plan_id', data.subscription.plan_id);
          }
          if (data.subscription.credits) {
            localStorage.setItem('sage_credits', String(data.subscription.credits));
            try { window.dispatchEvent(new Event('credits-updated')); } catch {}
          }
          if (data.subscription.expires_at) {
            localStorage.setItem('premium_expires', data.subscription.expires_at);
          }
          // Keep subscriptionData in sync — Future Reels 12-month drip reads
          // `started_at` / `created_at` from this key to compute which
          // 1/12 slice of content is currently unlocked. Without this, drip
          // logic falls back to month 1 for everyone forever.
          try {
            localStorage.setItem('subscriptionData', JSON.stringify(data.subscription));
          } catch (_) {}
        }
        
        return premium;
      }
      return false;
    } catch (e) {
      console.log('Refresh subscription error:', e);
      return false;
    }
  };

  // Poll after IAP/payment success to catch webhook lag (max 60s).
  // Frontend calls this right after purchase callback fires — we wait up to
  // 60 seconds for backend to receive the verification webhook and flip
  // user_subscriptions.status=active. Shows instant UI once detected.
  //
  // SINGLE-FLIGHT: multiple concurrent callers (iap-result + razorpay-success
  // events fire in parallel) are coalesced to a single active poller.
  const pollInFlightRef = useRef(false);
  const pollSubscriptionUntilPremium = async (maxSeconds = 60, intervalSeconds = 5) => {
    if (!user?.user_id) return false;
    if (pollInFlightRef.current) {
      rlog('poll_subscription_skipped_inflight', { user_id: user.user_id });
      return false;
    }
    pollInFlightRef.current = true;
    const startedAt = Date.now();
    const deadline = startedAt + maxSeconds * 1000;
    let attempt = 0;
    rlog('poll_subscription_start', { user_id: user.user_id, max_seconds: maxSeconds, interval_seconds: intervalSeconds });
    try {
      while (Date.now() < deadline) {
        attempt += 1;
        try {
          const premium = await refreshSubscription();
          if (premium) {
            const elapsed_s = ((Date.now()-startedAt)/1000).toFixed(1);
            console.log(`💎 Premium detected after ${attempt} poll(s) in ${elapsed_s}s`);
            rlog('poll_subscription_success', { user_id: user.user_id, attempts: attempt, elapsed_s });
            return true;
          }
        } catch (_) {}
        await new Promise((r) => setTimeout(r, intervalSeconds * 1000));
      }
      console.warn(`⚠️ pollSubscriptionUntilPremium: timed out after ${maxSeconds}s (${attempt} attempts)`);
      rlog('poll_subscription_timeout', { user_id: user.user_id, attempts: attempt, max_seconds: maxSeconds });
      return false;
    } finally {
      pollInFlightRef.current = false;
    }
  };

  // Update story data (cached in context, not localStorage)
  const updateStoryData = (data) => {
    setStoryData(data);
  };

  // Check session on mount
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Periodic content version check (every 5 minutes while app is active)
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      checkContentVersions();
    }, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Auto-refresh subscription when IAP/Razorpay purchase succeeds (catches webhook lag)
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleIapResult = (event) => {
      const detail = event.detail || {};
      rlog('iap_result_event', {
        success: !!detail.success,
        product_id: detail.product_id,
        transaction_id: detail.transaction_id,
        source: detail.source,
      });
      if (detail.success) {
        console.log('💰 IAP success event received — polling subscription...');
        // Immediate refresh + poll for up to 60s in case webhook hasn't fired yet
        refreshSubscription().catch(() => {});
        pollSubscriptionUntilPremium(60, 5).catch(() => {});
      }
    };
    const handleRazorpaySuccess = (ev) => {
      console.log('💰 Razorpay success event — polling subscription...');
      rlog('razorpay_success_event', {
        payment_id: ev?.detail?.payment_id,
        order_id: ev?.detail?.order_id,
        amount: ev?.detail?.amount,
      });
      refreshSubscription().catch(() => {});
      pollSubscriptionUntilPremium(60, 5).catch(() => {});
    };
    window.addEventListener('iap-result', handleIapResult);
    window.addEventListener('razorpay-payment-success', handleRazorpaySuccess);
    return () => {
      window.removeEventListener('iap-result', handleIapResult);
      window.removeEventListener('razorpay-payment-success', handleRazorpaySuccess);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.user_id]);

  const value = {
    // State (ALL from MongoDB)
    isLoading,
    isAuthenticated,
    user,
    // Canonical authenticated user_id — consumers should prefer this over
    // localStorage reads. Falls back to localStorage only if session user
    // hasn't hydrated yet, and NEVER returns a ghost/device id to prevent
    // authenticated requests from being attributed to anonymous buckets.
    userId: (() => {
      const candidate = user?.user_id || localStorage.getItem('user_id') || '';
      if (!candidate) return null;
      if (/^(native-|web-|session_|device_|taraanaya_device_)/.test(candidate)) return null;
      return candidate;
    })(),
    birthDetails,
    hasBirthDetails,
    isPremium,
    subscription,
    storyData,
    
    // Actions
    fetchSession,
    logout,
    updateSessionFromLogin,
    updatePremiumStatus,
    refreshSubscription,
    pollSubscriptionUntilPremium,
    updateStoryData,
    clearLocalState,
    checkContentVersions
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContext;
